/** @jsx jsx */
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import { Button } from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Popover } from '@apollo/space-kit/Popover';
import { jsx } from '@emotion/react';
import React, { useEffect, useState } from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { CheckWorkflowStatusWidget, LoadingPulse } from './CheckStatusWidget';
import { SchemaDiffMetaData } from './SchemaDiffMetaData';
import { SchemaDiffViewer } from './SchemaDiffViewer';

interface Props {
  source: {
    graphRef: GraphRef;
    schema: SchemaDiffMetaData;
  };
  contract: {
    graphRef: GraphRef;
    // The latest published contract schema (if exist)
    latestSchema?: SchemaDiffMetaData;
    // The (new) contract schema being proposed
    proposedSchema: SchemaDiffMetaData;
  };
  // The check workflow triggered for the proposed contract schema
  checkWorkflow?: GraphQLTypes.CheckWorkflowStatusWidgetFragment | null;
  // If true, displays a loading state until the 'checkWorkflow' field is non-null.
  checkWorkflowLoading?: Boolean;
}

export const ContractSchemaDiffViewer = ({
  source,
  contract,
  checkWorkflow,
  checkWorkflowLoading,
}: Props) => {
  // There exists the option to compare against the API schema or Supergraph schema.
  // The default behavior is compare against the API schema because it
  // reveals less line-by-line diffs then when comparing Supergraph schemas.
  const [compareAgainstAPISchema, setCompareAgainstAPISchema] = useState(true);

  // Default to comparing against the latest successful contract schema, if exist.
  // Comparing against the source variant will result in more line-line-diffs.
  const [compareAgainstLatest, setCompareAgainstLatest] = useState(
    Boolean(
      contract.latestSchema &&
        contract.latestSchema.apiSchema &&
        contract.latestSchema.supergraphSchema,
    ),
  );

  const [diffContent, setDiffContent] = useState<{
    originalSchema: string;
    originalFieldCount: number;
    originalTypeCount: number;

    modifiedSchema: string;
    modifiedFieldCount: number;
    modifiedTypeCount: number;
  }>();

  useEffect(() => {
    if (compareAgainstAPISchema) {
      if (compareAgainstLatest && contract?.latestSchema) {
        setDiffContent({
          originalSchema: contract.latestSchema.apiSchema,
          originalFieldCount: contract.latestSchema.fieldCount,
          originalTypeCount: contract.latestSchema.typeCount,

          modifiedSchema: contract.proposedSchema.apiSchema,
          modifiedFieldCount: contract.proposedSchema.fieldCount,
          modifiedTypeCount: contract.proposedSchema.typeCount,
        });
      } else {
        setDiffContent({
          originalSchema: source.schema.apiSchema,
          originalFieldCount: source.schema.fieldCount,
          originalTypeCount: source.schema.typeCount,

          modifiedSchema: contract.proposedSchema.apiSchema,
          modifiedFieldCount: contract.proposedSchema.fieldCount,
          modifiedTypeCount: contract.proposedSchema.typeCount,
        });
      }
    } else if (compareAgainstLatest && contract?.latestSchema) {
      setDiffContent({
        originalSchema: contract.latestSchema.supergraphSchema,
        originalFieldCount: contract.latestSchema.fieldCount,
        originalTypeCount: contract.latestSchema.typeCount,

        modifiedSchema: contract.proposedSchema.supergraphSchema,
        modifiedFieldCount: contract.proposedSchema.fieldCount,
        modifiedTypeCount: contract.proposedSchema.typeCount,
      });
    } else {
      setDiffContent({
        originalSchema: source.schema.supergraphSchema,
        originalFieldCount: source.schema.fieldCount,
        originalTypeCount: source.schema.typeCount,

        modifiedSchema: contract.proposedSchema.supergraphSchema,
        modifiedFieldCount: contract.proposedSchema.fieldCount,
        modifiedTypeCount: contract.proposedSchema.typeCount,
      });
    }
  }, [compareAgainstAPISchema, compareAgainstLatest, source, contract]);

  return (
    <div>
      {/* Controls */}
      <div className="hide-scrollbar flex items-center overflow-x-auto rounded-t border border-b-0 border-primary bg-neutral p-2">
        <span className="ml-1 mr-2 text-sm font-semibold text-neutral">
          Base
        </span>
        <VariantPickerDropdown
          sourceVariant={source.graphRef.graphVariant}
          contractVariant={contract.graphRef.graphVariant}
          hasLatest={Boolean(
            contract.latestSchema?.apiSchema &&
              contract.latestSchema?.supergraphSchema,
          )}
          compareAgainstLatest={compareAgainstLatest}
          setCompareAgainstLatest={setCompareAgainstLatest}
        />
        <Tooltip label="Comparing against">
          <IconArrowLeft className="mx-3 text-neutral hover:text-neutral" />
        </Tooltip>
        <Tooltip label="Proposed contract schema" placement="top-start">
          <div
            className="mr-4 w-fit truncate text-sm font-semibold text-neutral"
            title={
              contract.graphRef.graphVariant.length > 35
                ? `${contract.graphRef.graphVariant} (preview)`
                : undefined
            }
          >
            {contract.graphRef.graphVariant} (preview)
          </div>
        </Tooltip>
        <div className="ml-auto flex">
          {checkWorkflow ? (
            <CheckWorkflowStatusWidget
              className="mr-1"
              checkWorkflow={checkWorkflow}
            />
          ) : checkWorkflowLoading ? (
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<LoadingPulse className="mr-1" />}
            >
              Checks
            </Button>
          ) : null}
          <SchemaTypePickerDropdown
            compareAgainstAPISchema={compareAgainstAPISchema}
            setCompareAgainstAPISchema={setCompareAgainstAPISchema}
          />
        </div>
      </div>
      {/* Diff Viewer */}
      {diffContent && (
        <SchemaDiffViewer
          originalSchema={diffContent.originalSchema}
          modifiedSchema={diffContent.modifiedSchema}
          originalFieldCount={diffContent.originalFieldCount}
          originalTypeCount={diffContent.originalTypeCount}
          modifiedFieldCount={diffContent.modifiedFieldCount}
          modifiedTypeCount={diffContent.modifiedTypeCount}
          downloadedSdlFilename={`${contract.graphRef.graphId}@${contract.graphRef.graphVariant}`}
        />
      )}
    </div>
  );
};

function VariantPickerDropdown({
  contractVariant,
  sourceVariant,
  hasLatest,
  compareAgainstLatest,
  setCompareAgainstLatest,
}: {
  contractVariant: string;
  sourceVariant: string;
  hasLatest: boolean;
  compareAgainstLatest: boolean;
  setCompareAgainstLatest: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <Popover
      placement="bottom-start"
      content={
        <List>
          {hasLatest && (
            <Tooltip
              label="Latest contract schema publication"
              placement="right"
            >
              <ListItem
                key="latest-schema"
                selected={compareAgainstLatest}
                onClick={() => setCompareAgainstLatest(true)}
              >
                <div
                  title={
                    contractVariant.length > 35
                      ? `${contractVariant} (latest)`
                      : undefined
                  }
                >
                  {contractVariant} (latest)
                </div>
              </ListItem>
            </Tooltip>
          )}
          <Tooltip label="Source variant of this contract" placement="right">
            <ListItem
              key="source-schema"
              selected={!compareAgainstLatest}
              onClick={() => setCompareAgainstLatest(false)}
            >
              <div
                title={
                  sourceVariant.length > 35
                    ? `${sourceVariant} (source)`
                    : undefined
                }
              >
                {sourceVariant} (source)
              </div>
            </ListItem>
          </Tooltip>
        </List>
      }
      trigger={
        <div>
          <DropdownButton
            label={
              compareAgainstLatest
                ? `${contractVariant} (latest)`
                : `${sourceVariant} (source)`
            }
          />
        </div>
      }
    />
  );
}

function SchemaTypePickerDropdown({
  compareAgainstAPISchema,
  setCompareAgainstAPISchema,
  className,
}: {
  compareAgainstAPISchema: boolean;
  setCompareAgainstAPISchema: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
}) {
  return (
    <Popover
      placement="bottom-end"
      content={
        <List>
          <Tooltip
            label="Used by clients as your actual GraphQL API"
            placement="left"
          >
            <ListItem
              key="api-schema"
              selected={compareAgainstAPISchema}
              onClick={() => setCompareAgainstAPISchema(true)}
            >
              API Schema
            </ListItem>
          </Tooltip>
          <Tooltip label="Used by routers and tooling" placement="left">
            <ListItem
              key="supergraph-schema"
              selected={!compareAgainstAPISchema}
              onClick={() => setCompareAgainstAPISchema(false)}
            >
              Supergraph Schema
            </ListItem>
          </Tooltip>
        </List>
      }
      trigger={
        <div className={className}>
          <DropdownButton
            label={compareAgainstAPISchema ? 'API Schema' : 'Supergraph Schema'}
          />
        </div>
      }
    />
  );
}

function DropdownButton({
  label,
  disabled,
  tooltipContent,
}: {
  label: string;
  disabled?: boolean;
  tooltipContent?: React.ReactNode;
}) {
  return (
    <Tooltip
      disabled={tooltipContent === undefined}
      label={tooltipContent}
      placement="top-start"
    >
      <div>
        <Button
          size="sm"
          variant="secondary"
          isDisabled={disabled}
          rightIcon={<IconChevronDown />}
        >
          <div className="max-w-56 truncate">{label}</div>
        </Button>
      </div>
    </Tooltip>
  );
}
